import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Link from '@mui/material/Link';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import React, { ChangeEvent, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { api, urlBase } from '../api';

function Copyright(props: any) {
    return (
        <Typography variant="body2" color="text.secondary" align="center" {...props}>
            {'Direitos Reservados © Backup Fácil '}
            {new Date().getFullYear()}
            {'.'}
        </Typography>
    );
}

const theme = createTheme({
    palette: {
        primary: {
            main: '#2563eb', // Azul premium moderno
        },
        secondary: {
            main: '#4f46e5',
        },
    },
});

export default function ValidacaoTokenDownload() {
    const { id } = useParams<{ id: string }>();
    const [token, setToken] = useState("");
    const [solicitando, setSolicitando] = useState(true);
    const [validando, setValidando] = useState(false);
    const [erro, setErro] = useState<string | null>(null);
    const [sucesso, setSucesso] = useState(false);
    const [mensagemSucesso, setMensagemSucesso] = useState("");

    const solicitarToken = React.useCallback(async () => {
        setSolicitando(true);
        setErro(null);
        try {
            await api.post(`/v2/arquivo/solicitar-download/${id}`);
            setSolicitando(false);
        } catch (error: any) {
            console.error(error);
            const msg = error.response?.data?.mensagem || "Não foi possível enviar o código de verificação. Verifique se o arquivo existe ou contate o suporte.";
            setErro(msg);
            setSolicitando(false);
        }
    }, [id]);

    useEffect(() => {
        if (id) {
            solicitarToken();
        } else {
            setErro("Identificador de arquivo inválido.");
            setSolicitando(false);
        }
    }, [id, solicitarToken]);

    const handleTokenChange = (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        const val = event.target.value.replace(/\D/g, ""); // Apenas dígitos
        if (val.length <= 6) {
            setToken(val);
            setErro(null);
        }
    };

    const handleValidar = async (event: React.FormEvent) => {
        event.preventDefault();
        if (token.length !== 6) return;

        setValidando(true);
        setErro(null);
        try {
            const res = await api.post(`/v2/arquivo/validar-download/${id}`, { token });
            setValidando(false);
            setSucesso(true);
            setMensagemSucesso("Código validado com sucesso! Iniciando download...");
            
            // Redireciona o navegador para o link de download
            const downloadLink = res.data.link;
            
            // Se for um link relativo (retornado pela rota do servidor REST), concatena o host
            if (downloadLink.startsWith("/api/")) {
                const host = urlBase.replace("/api", "");
                window.location.href = host + downloadLink;
            } else {
                window.location.href = downloadLink;
            }
        } catch (error: any) {
            console.error(error);
            const msg = error.response?.data?.mensagem || "Erro ao validar o código. Verifique se o número está correto e tente novamente.";
            setErro(msg);
            setValidando(false);
        }
    };

    return (
        <ThemeProvider theme={theme}>
            <Container component="main" maxWidth="xs">
                <CssBaseline />
                <Box
                    sx={{
                        marginTop: 12,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >
                    <Paper
                        elevation={4}
                        sx={{
                            padding: 4,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            borderRadius: 3,
                            background: 'rgba(255, 255, 255, 0.85)',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            width: '100%'
                        }}
                    >
                        <Box sx={{ m: 1, bgcolor: 'primary.main', borderRadius: '50%', p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <CloudQueueIcon sx={{ fontSize: 32, color: '#fff' }} />
                        </Box>
                        
                        <Typography component="h1" variant="h5" sx={{ mt: 1, fontWeight: 'bold' }}>
                            Validação de Download
                        </Typography>

                        <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1, mb: 3 }}>
                            Para sua segurança, enviamos um código de 2 fatores ao e-mail cadastrado.
                        </Typography>

                        {solicitando && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', my: 3 }}>
                                <CircularProgress size={32} sx={{ mb: 2 }} />
                                <Typography variant="body2" color="text.secondary">
                                    Solicitando código de verificação...
                                </Typography>
                            </Box>
                        )}

                        {!solicitando && !sucesso && (
                            <Box component="form" onSubmit={handleValidar} noValidate sx={{ mt: 1, width: '100%' }}>
                                <TextField
                                    margin="normal"
                                    required
                                    fullWidth
                                    name="token"
                                    label="Código de Verificação (6 dígitos)"
                                    type="text"
                                    id="token"
                                    autoFocus
                                    value={token}
                                    onChange={handleTokenChange}
                                    placeholder="000000"
                                    inputProps={{ 
                                        maxLength: 6,
                                        style: { textAlign: 'center', letterSpacing: '4px', fontSize: '20px', fontWeight: 'bold' } 
                                    }}
                                    disabled={validando}
                                />

                                {erro && (
                                    <Alert severity="error" sx={{ mt: 2, mb: 1, width: '100%' }}>
                                        {erro}
                                    </Alert>
                                )}

                                <Button
                                    type="submit"
                                    fullWidth
                                    variant="contained"
                                    disabled={token.length !== 6 || validando}
                                    sx={{ mt: 3, mb: 2, py: 1.2, fontWeight: 'bold' }}
                                >
                                    {validando ? <CircularProgress size={24} color="inherit" /> : 'Confirmar e Baixar'}
                                </Button>

                                <Grid container justifyContent="center">
                                    <Grid item>
                                        <Link href="#" variant="body2" onClick={(e) => { e.preventDefault(); solicitarToken(); }}>
                                            Não recebeu? Reenviar código
                                        </Link>
                                    </Grid>
                                </Grid>
                            </Box>
                        )}

                        {sucesso && (
                            <Box sx={{ mt: 2, width: '100%' }}>
                                <Alert severity="success" sx={{ width: '100%', mb: 2 }}>
                                    {mensagemSucesso}
                                </Alert>
                                <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                                    <CircularProgress size={24} />
                                </Box>
                            </Box>
                        )}
                    </Paper>
                </Box>
                <Copyright sx={{ mt: 8, mb: 4 }} />
            </Container>
        </ThemeProvider>
    );
}
